import { messagePreviewText, Tooltip } from '@odo/shared-ui';
import { conversationRowKey, type ConversationRow } from '../../services/conversations';
import { CONVERSATION_STATUS_COLORS } from './ConversationStatusBadge';
import { useConversationActions } from '../../hooks/useConversationActions';
import { PatientPracticeCell } from '../shared/PatientPracticeCell';
import { Avatar } from '../Avatar';
import AssigneeSelector from './AssigneeSelector';
import { useRoleNamesForId } from '../../hooks/useRoles';
import { formatCompactRecency, formatDateTime } from '../../utils/dates';

type Actions = ReturnType<typeof useConversationActions>;

export function PatientCell({ row }: { row: ConversationRow }) {
    return (
        <div className="flex items-center gap-4 min-w-0">
            {/* Same fill as the row's STATUS badge, so the dot reads as that thread kind. */}
            <div
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{
                    backgroundColor: row.unread
                        ? CONVERSATION_STATUS_COLORS[row.status]
                        : 'transparent'
                }}
            />
            <PatientPracticeCell
                patientName={row.name}
                nameClassName="text-sm font-normal leading-4 tracking-[0.005em] text-gray-800 underline cursor-pointer truncate min-w-0"
                avatar={
                    <Avatar
                        user={{ name: row.name, email: '' }}
                        thumbnailURL={row.profileThumbnailUrl ?? undefined}
                    />
                }
                practiceName={row.practiceName}
                practiceId={row.practiceId}
                doctorName={row.practiceDoctor}
                format="doctor-practice"
                sublineToneClassName="text-gray-500"
            />
        </div>
    );
}

interface AssignedCellProps {
    row: ConversationRow;
    currentUserEmail: string;
    onAssign: (email: string) => void;
    onUnassign: () => void;
}

// Offers the members of the thread's team only.
export function AssignedCell({ row, currentUserEmail, onAssign, onUnassign }: AssignedCellProps) {
    const teamRoles = useRoleNamesForId(row.teamRoleId);
    return (
        <AssigneeSelector
            assignedTo={row.assignedTo}
            roles={teamRoles}
            onAssign={onAssign}
            onUnassign={onUnassign}
            currentUserEmail={currentUserEmail}
        />
    );
}

export function LastMessageCell({ row }: { row: ConversationRow }) {
    if (!row.lastMessageText) return <span className="text-gray-400">-</span>;
    return (
        <div className="truncate text-gray-500">
            <span className="text-sm text-gray-800 font-medium">
                {messagePreviewText(row.lastMessageText)}
            </span>
        </div>
    );
}

export function DateCell({ iso }: { iso: string | null }) {
    return (
        <Tooltip content={formatDateTime(iso)}>
            <span>{formatCompactRecency(iso, Date.now())}</span>
        </Tooltip>
    );
}

interface MarkReadUnreadCellProps {
    row: ConversationRow;
    markingSeenKey: Actions['markingSeenKey'];
    markingUnreadKey: Actions['markingUnreadKey'];
    onMarkSeen: Actions['handleMarkAsSeen'];
    onMarkUnread: Actions['handleMarkAsUnread'];
}

export function MarkReadUnreadCell({
    row,
    markingSeenKey,
    markingUnreadKey,
    onMarkSeen,
    onMarkUnread
}: MarkReadUnreadCellProps) {
    // A row is (patientId, status), so the in-flight marker is keyed on both.
    const rowKey = conversationRowKey(row);
    if (row.unread) {
        return (
            <button
                type="button"
                onClick={(e) => onMarkSeen(e, row)}
                disabled={markingSeenKey === rowKey}
                className="inline-flex items-center justify-center gap-1.5 max-w-full text-xs font-semibold text-green-700 bg-green-50 border border-green-200 rounded-lg px-2.5 py-1 hover:bg-green-100 transition-colors disabled:opacity-50"
            >
                {markingSeenKey === rowKey ? (
                    <div className="h-3.5 w-3.5 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
                ) : (
                    <>
                        <svg className="w-3.5 h-3.5 flex-shrink-0" viewBox="0 0 14 14" fill="none">
                            <path
                                d="M5.25 6.41732L7 8.16732L12.8333 2.33398"
                                stroke="currentColor"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                        <span className="truncate">Mark as read</span>
                    </>
                )}
            </button>
        );
    }
    return (
        <button
            type="button"
            onClick={(e) => onMarkUnread(e, row)}
            disabled={markingUnreadKey === rowKey}
            className="inline-flex items-center justify-center gap-1.5 max-w-full text-xs font-medium text-orange-600 bg-orange-50 border border-orange-200 rounded-lg px-2.5 py-1 hover:bg-orange-100 transition-colors disabled:opacity-50"
        >
            {markingUnreadKey === rowKey ? (
                <div className="h-3 w-3 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" />
            ) : (
                <span className="truncate">Mark as unread</span>
            )}
        </button>
    );
}
